/**
 * Whether one commerce connection's revenue counts toward a BLENDED figure —
 * revenue, ROAS or MER computed across every connected store at once.
 *
 * 🔴 NOTHING COMPUTES A BLENDED FIGURE YET. Searched `src/lib/analytics/` and
 * `src/hooks/analytics/` on 2026-08-27: no blended-revenue, ROAS or MER
 * calculation exists in this codebase today. This module is the PREFERENCE a
 * merchant sets now, ahead of that calculation being built — not a control
 * that changes a number currently on screen. Say so in the UI copy rather than
 * implying an effect that does not exist yet; a switch that visibly does
 * nothing is the silent no-op bug class this repo has hit before
 * (`feedback_silent_no_op_bug_class.md`).
 *
 * 🔴 Stored in `user_connections.metadata`, not a new column. `metadata` is
 * `Json | null` and already carries other per-connection flags read nowhere
 * else but here — `token_expires_at`, `team_name`, `account_email`
 * (`usePlatformIntegrations.tsx`) — so this follows the existing shape rather
 * than adding a migration for one boolean.
 *
 * Default is TRUE (included) when the key is absent — an unmodified store
 * counts toward the rollup until a merchant deliberately excludes it, which
 * matches "included by default, opt out" rather than the reverse.
 */
export const REVENUE_ROLLUP_METADATA_KEY = 'include_in_revenue_rollup';

/**
 * Reads the flag off a connection's raw `metadata` column. Anything other than
 * the literal boolean `false` resolves to included — a malformed or
 * unrecognized value must never silently exclude a store's revenue.
 */
export function resolveIncludeInRevenueRollup(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== 'object') return true;
  const value = (metadata as Record<string, unknown>)[REVENUE_ROLLUP_METADATA_KEY];
  return value !== false;
}

/** Merges the flag into an existing metadata object without dropping other keys. */
export function withRevenueRollupInclusion(
  metadata: unknown,
  include: boolean,
): Record<string, unknown> {
  const base =
    metadata && typeof metadata === 'object' ? (metadata as Record<string, unknown>) : {};
  return { ...base, [REVENUE_ROLLUP_METADATA_KEY]: include };
}
